import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { CSS_RESTRICTIONS } from '@common/constants';
import type { CardSettingsFormGroup } from '@web/widgets/catalog-products/type';
import { type CardSettingsFormType } from '@web/widgets/catalog-products/type';
import { ELineStyle } from '@common/enums';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { GeneralSettingsFormType } from '@web/widgets/product-card/type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-card-settings',
    templateUrl: './card-settings.component.html',
    styleUrls: ['./card-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class CardSettingsComponent {
    @Input() cardSettingsFormGroup: CardSettingsFormGroup;

    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public setBorderRadius(value: number): void {
        this.cardSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setPadding(value: number): void {
        this.cardSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.padding.patchValue(value);
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.cardSettingsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public setBorderWidth(value: number): void {
        this.borderWidth.patchValue(value);
    }

    public setBorderColor({ value }: ColorPickerEventPayload): void {
        this.borderColor.patchValue(value);
    }

    public get backgroundColor(): CardSettingsFormType['backgroundColor'] {
        return this.cardSettingsFormGroup.get('backgroundColor') as CardSettingsFormType['backgroundColor'];
    }

    public get borderRadius(): CardSettingsFormType['borderRadius'] {
        return this.cardSettingsFormGroup.get('borderRadius') as CardSettingsFormType['borderRadius'];
    }

    public get padding(): CardSettingsFormType['padding'] {
        return this.cardSettingsFormGroup.get('padding') as CardSettingsFormType['padding'];
    }

    public get isBorder(): GeneralSettingsFormType['isBorder'] {
        return this.cardSettingsFormGroup.get('isBorder') as GeneralSettingsFormType['isBorder'];
    }

    public get borderWidth(): GeneralSettingsFormType['borderWidth'] {
        return this.cardSettingsFormGroup.get('borderWidth') as GeneralSettingsFormType['borderWidth'];
    }

    public get borderStyle(): GeneralSettingsFormType['borderStyle'] {
        return this.cardSettingsFormGroup.get('borderStyle') as GeneralSettingsFormType['borderStyle'];
    }

    public get borderColor(): GeneralSettingsFormType['borderColor'] {
        return this.cardSettingsFormGroup.get('borderColor') as GeneralSettingsFormType['borderColor'];
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get PADDING_MIN(): number {
        return CSS_RESTRICTIONS.PADDING_MIN;
    }

    public get PADDING_MAX(): number {
        return CSS_RESTRICTIONS.PADDING_MAX;
    }

    public get SIZE_MIN(): number {
        return CSS_RESTRICTIONS.SIZE_MIN;
    }

    public get SIZE_MAX(): number {
        return CSS_RESTRICTIONS.SIZE_MAX;
    }

    protected readonly lineStyle = ELineStyle;
}
